export type Tone = 'pink' | 'green' | 'amber' | 'slate' | 'rose' | 'blue';

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** AI costs are fractions of a cent per call, so show enough precision to compare companies. */
export function formatUsd(value: number): string {
  if (!value) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

export function formatTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return String(value);
}

export function formatPct(value: number | null | undefined): string {
  return value == null ? '—' : `${value}%`;
}

export function pctTone(value: number | null | undefined): Tone {
  if (value == null) return 'slate';
  if (value >= 85) return 'green';
  if (value >= 65) return 'amber';
  return 'rose';
}

export function flagEmoji(countryCode?: string | null): string {
  if (!countryCode || countryCode.length !== 2) return '🏳';
  return String.fromCodePoint(...[...countryCode.toUpperCase()].map((ch) => 0x1f1a5 + ch.charCodeAt(0)));
}

/** Prefer the unique Micus display name over the real/legal company name. */
export function companyDisplayName(c: { logicalName?: string | null; name: string }): string {
  return c.logicalName || c.name;
}

export const BAND: Record<'GOOD' | 'AVERAGE' | 'BAD', { label: string; tone: Tone; hint: string }> = {
  BAD: { label: 'Bad', tone: 'rose', hint: 'AI score below 50' },
  AVERAGE: { label: 'Average', tone: 'amber', hint: 'AI score 50–69' },
  GOOD: { label: 'Good', tone: 'green', hint: 'AI score 70 or above' },
};

export const COMPANY_STATUS: Record<string, { label: string; tone: Tone }> = {
  INVITED: { label: 'Invited', tone: 'amber' },
  INTAKE: { label: 'Intake', tone: 'pink' },
  SUBMITTED: { label: 'Submitted', tone: 'blue' },
  RATED: { label: 'Rated', tone: 'green' },
  REMEDIATING: { label: 'Remediating', tone: 'amber' },
  REANALYSIS: { label: 'Re-analysis', tone: 'blue' },
  READY: { label: 'Ready', tone: 'green' },
  OUTREACH: { label: 'Partner outreach', tone: 'green' },
  FUNDED: { label: 'Funded', tone: 'green' },
};

export const VERDICT: Record<string, { label: string; tone: Tone }> = {
  VERIFIED: { label: 'Verified', tone: 'green' },
  NEEDS_REVIEW: { label: 'Needs review', tone: 'amber' },
  REJECTED: { label: 'Rejected', tone: 'rose' },
  NOT_AI_READABLE: { label: 'Not AI-readable', tone: 'slate' },
  AI_FAILED: { label: 'AI failed', tone: 'rose' },
  PENDING: { label: 'Pending', tone: 'blue' },
  VERIFYING: { label: 'AI verifying', tone: 'blue' },
  UPLOADED: { label: 'Uploaded', tone: 'slate' },
  SUPERSEDED: { label: 'Replaced', tone: 'slate' },
};

export const FEATURE: Record<string, string> = {
  DOCUMENT_VERIFICATION: 'Document verification',
  RATING: 'Rating generation',
  ANALYST_COPILOT: 'Analyst copilot',
  PROMPT_TEST: 'Prompt tests',
  DEMO_DOC_GENERATION: 'Demo document generation',
};

export const PROMPT_ROLE: Record<string, string> = {
  LEAD_ANALYST: 'Lead analyst',
  GROUP_FINANCIAL: 'Financial agent',
  GROUP_LEGAL: 'Legal agent',
  GROUP_KYC: 'KYC agent',
  GROUP_BUSINESS: 'Business agent',
  DOCUMENT_VERIFICATION: 'Document verification',
  ANALYST_COPILOT: 'Analyst copilot',
};

export const RESOLVER: Record<string, string> = {
  active: 'Live',
  canary: 'Canary',
  draft: 'Draft test',
  fallback: 'Platform default',
  pin: 'Pinned',
};

export const SMITH_OUTCOME: Record<string, { label: string; tone: Tone; hint: string }> = {
  GOOD: { label: 'Good rating', tone: 'green', hint: 'AI score ≥ 70, accepted as drafted' },
  AVERAGE: { label: 'Average', tone: 'amber', hint: 'AI score 50–69' },
  BAD: { label: 'Bad rating', tone: 'rose', hint: 'AI score below 50' },
  UNABLE_TO_FIX: { label: 'Unable to fix', tone: 'rose', hint: 'Historic or factual findings — new uploads cannot lift the grade' },
  ERROR: { label: 'AI error', tone: 'rose', hint: 'The model call failed' },
  NEUTRAL: { label: 'Trace', tone: 'slate', hint: 'Pipeline run without a rating outcome' },
};

export function formatMs(value?: number | null): string {
  if (value == null || value <= 0) return '—';
  if (value < 1000) return `${Math.round(value)}ms`;
  return `${(value / 1000).toFixed(1)}s`;
}

export function smithRunLabel(name: string): string {
  return name.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}
